import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import Banner from "../assets/images/banners/news.jpg";
import imageMob from "../assets/images/banners/news-mob.jpg";
import BuyTicket from "../assets/images/icons/BuyTicket.svg";
import { useOutletContext, useParams } from "react-router-dom";
import { handleApiCall } from "../apis";
import { endpoints } from "../utils/endpoints";
import { useLanguage } from "../context/LanguageContext";
import { getLocalizedFieldValue } from "../utils/language";
import { UI_CONSTANTS } from "../utils/constants";
import parse from "html-react-parser";
import he from "he";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import "@splidejs/react-splide/css";


const slugify = (text = "") =>
  text
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")     // spaces → -
    .replace(/[^\w\u0600-\u06FF-]+/g, "") // allow Arabic
    .replace(/--+/g, "-")
    .replace(/^-+|-+$/g, "");


const normalizePartnerUpdates = (data) => {
  if (!data) return [];
  if (Array.isArray(data?.partnerUpdate)) return data.partnerUpdate;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;
  return [];
};


function PartnerUpdateDetail() {
  const { setBannerData } = useOutletContext();
  // const { id } = useParams();
  const { slug } = useParams();

  const { language } = useLanguage();
  const [isFixed, setIsFixed] = useState(true);
  const parentRef = useRef(null);
  const [partnerUpdateData, setPartnerUpdateData] = useState(null);
  const [weekendData, setWeekendData] = useState([]);
  const [loading, setLoading] = useState(false);



const fetchPartnerUpdateDetails = useCallback(async () => {
  if (!slug) return;
  setLoading(true);

  try {
    // Fetch all partner updates
    const listRes = await handleApiCall("get", endpoints.partnerUpdateList);
    const list = normalizePartnerUpdates(listRes?.data?.data);

    // Match slug with title
    // const matchedItem = list.find(item => {
    //   const title =
    //     getLocalizedFieldValue(item, language, "title") || "";
    //   return slugify(title) === slug;
    // });

    const matchedItem = list.find(item => {
      const enSlug = slugify(item.title_en || "");
      const arSlug = slugify(item.title_ar || "");
      return slug === enSlug || slug === arSlug;
    });

    if (!matchedItem?._id) {
      setPartnerUpdateData(null);
      return;
    }

    // Fetch details using ID
    const detailRes = await handleApiCall(
      "get",
      `${endpoints.userPartnerUpdateDetails}/${matchedItem._id}`
    );

    if (
      detailRes?.data?.code === 200 ||
      detailRes?.data?.success === true
    ) {
      const partnerUpdate = detailRes?.data?.data?.partner_update || null;
      const weekend = detailRes?.data?.data?.weekend_data || [];

      setPartnerUpdateData(partnerUpdate);
      setWeekendData(weekend);

      const bannerTitle =
        getLocalizedFieldValue(partnerUpdate, language, "title") || "";

      setBannerData({
        image: partnerUpdate?.desktop_image || Banner,
        MobileBanner: partnerUpdate?.mobile_image || imageMob,
        title: bannerTitle,
        subtitle: "",
        skew: true,
        bannershow: true,
      });
    }
  } catch (error) {
    console.error("Error fetching partner update details:", error);
    setPartnerUpdateData(null);
  } finally {
    setLoading(false);
  }
}, [slug, language, setBannerData]);


  // const fetchPartnerUpdateDetails = useCallback(async () => {
  //   if (!id) return;
  //   setLoading(true);
  //   try {
  //     const response = await handleApiCall("get", `${endpoints.userPartnerUpdateDetails}/${id}`);
  //     if (
  //       response?.data?.code === 200 ||
  //       response?.data?.code === "200" ||
  //       response?.data?.success === true
  //     ) {
  //       const partnerUpdate = response?.data?.data?.partner_update || null;
  //       const weekend = response?.data?.data?.weekend_data || [];
  //       setPartnerUpdateData(partnerUpdate);
  //       setWeekendData(weekend);
        
  //       // Set banner title and images
  //       if (partnerUpdate) {
  //         const bannerTitle = getLocalizedFieldValue(partnerUpdate, language, "title") || "";
  //         const desktopImage = partnerUpdate?.desktop_image || Banner;
  //         const mobileImage = partnerUpdate?.mobile_image || imageMob;
  //         setBannerData({
  //           image: desktopImage,
  //           MobileBanner: mobileImage,
  //           title: bannerTitle,
  //           subtitle: "",
  //           skew: true,
  //           bannershow: true,
  //         });
  //       }
  //     } else {
  //       setPartnerUpdateData(null);
  //       setWeekendData([]);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching partner update details:", error);
  //     setPartnerUpdateData(null);
  //     setWeekendData([]);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [id, language, setBannerData]);

  useEffect(() => {
    // Set initial banner (will be updated when partner update data loads)
    setBannerData({
      image: Banner,
      MobileBanner: imageMob,
      title: "",
      subtitle: "",
      skew: true,
      bannershow: true,
    });
    fetchPartnerUpdateDetails();
  }, [fetchPartnerUpdateDetails, setBannerData]);

  useEffect(() => {
    const handleScroll = () => {
      if (!parentRef.current) return;
      const rect = parentRef.current.getBoundingClientRect();
      setIsFixed(rect.bottom > window.innerHeight);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Memoized localized values
  const detailPageTitle = useMemo(() => {
    if (!partnerUpdateData) return "";
    return getLocalizedFieldValue(partnerUpdateData, language, "detail_page_title") || "";
  }, [partnerUpdateData, language]);

  const description = useMemo(() => {
    if (!partnerUpdateData) return "";
    const desc = getLocalizedFieldValue(partnerUpdateData, language, "description") || "";
    return desc ? he.decode(desc) : "";
  }, [partnerUpdateData, language]);

  const initialTitle = useMemo(() => {
    if (!partnerUpdateData) return "";
    return getLocalizedFieldValue(partnerUpdateData, language, "initial_title") || "";
  }, [partnerUpdateData, language]);

  const initialDescription = useMemo(() => {
    if (!partnerUpdateData) return "";
    const desc = getLocalizedFieldValue(partnerUpdateData, language, "initial_description") || "";
    return desc ? he.decode(desc) : "";
  }, [partnerUpdateData, language]);

  // API may return initial_image as a string or an array of URLs
  const initialImages = useMemo(() => {
    const raw = partnerUpdateData?.initial_image;
    const images = (Array.isArray(raw) ? raw : raw ? [raw] : [])
      .filter((src) => typeof src === "string" && src.trim())
      .map((src) => src.trim().replace(/([^:]\/)\/+/g, "$1"));
    return images;
  }, [partnerUpdateData]);

  const middleTitle = useMemo(() => {
    if (!partnerUpdateData) return "";
    return getLocalizedFieldValue(partnerUpdateData, language, "middle_title") || "";
  }, [partnerUpdateData, language]);

  const middleDescription = useMemo(() => {
    if (!partnerUpdateData) return "";
    const desc = getLocalizedFieldValue(partnerUpdateData, language, "middle_description") || "";
    return desc ? he.decode(desc) : "";
  }, [partnerUpdateData, language]);

  const bottomDescription = useMemo(() => {
    if (!partnerUpdateData) return "";
    const desc = getLocalizedFieldValue(partnerUpdateData, language, "bottom_description") || "";
    return desc ? he.decode(desc) : "";
  }, [partnerUpdateData, language]);

  // Get buy ticket URL based on language
  const buyTicketUrl = useMemo(() => {
    if (!partnerUpdateData) return "";
    return language === "AR" 
      ? partnerUpdateData?.buy_ticket_ar || partnerUpdateData?.buy_ticket_en || ""
      : partnerUpdateData?.buy_ticket_en || partnerUpdateData?.buy_ticket_ar || "";
  }, [partnerUpdateData, language]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}`;
  };

  if (loading) {
    return (
      <div className="container max-w-8xl mx-auto px-2 py-8 md:py-0 md:px-8 2xl:px-32 text-white text-center">
        <p>{UI_CONSTANTS.BUTTON_LABELS.LOADING || "Loading..."}</p>
      </div>
    );
  }

  if (!partnerUpdateData) {
    return (
      <div className="container max-w-8xl mx-auto px-2 py-8 md:py-0 md:px-8 2xl:px-32 text-white text-center">
        <p>{language === "AR" ? "لا توجد بيانات متاحة حالياً." : "No data available at the moment."}</p>
      </div>
    );
  }

  return (
    <>
      <div className="container max-w-8xl mx-auto px-0 py-8 md:py-0 md:px-8 2xl:px-32 relative" ref={parentRef}>
        <div className="mb-10 md:mb-20 text-white [&_h2]:text-[1.75rem] md:[&_h2]:text-[2.625rem] [&_h2]:font-SaudiaRegular [&_h3]:text-2xl [&_h3]:font-SaudiaRegular md:[&_p]:text-lg">
          <div className="grid grid-cols-12">
            <div className="gap-x-16 col-span-12 md:col-span-8">
              {detailPageTitle && <h2 className="text-[2.625rem] mb-5 md:mb-10">{detailPageTitle}</h2>}
              {description && (
                <div className="mb-10 font-SaudiaLight">{parse(description)}</div>
              )}
              {initialTitle && <h3 className="mb-4">{initialTitle}</h3>}
              {initialDescription && (
                <div className="mb-10">{parse(initialDescription)}</div>
              )}
            </div>
          </div>
          {initialImages.length > 0 && (
            <div className={`max-w-8xl mx-auto overflow-hidden`}>
              <div className="
              onlyPagination
              [&_.splide\_\_track]:!overflow-visible 
              mb-12 
              md:mb-12 
              [&_.splide\_\_arrows]:!hidden 
              [&_.splide\_\_pagination]:!absolute 
              [&_.splide\_\_pagination]:-bottom-8
              [&_.splide\_\_pagination]:left-0 
              [&_.splide\_\_pagination]:right-0 
              [&_.splide\_\_pagination]:!flex">
                <div className={`nagative pt-6 ${language === "AR" ? "" : ""} ${initialImages.length > 1 ? "md:pt-14" : ""}`}
                >
                  <Splide
                    key={`${language}-${initialImages.length}`}
                    options={{
                      rewind: false,
                      gap: "28px",
                      perPage: 2,
                      autoWidth: true,
                      focus: 0,
                      start: 0,
                      perMove: 1,
                      arrows: initialImages.length > 1,
                      pagination: initialImages.length > 1,
                      direction: language === "AR" ? "rtl" : "ltr",
                      breakpoints: {
                        767: {
                          gap: "12px",
                        },
                      },
                    }}
                    aria-label={initialTitle || "Partner update images"}
                  >
                    {initialImages.map((src, index) => (
                      <SplideSlide
                        key={`${src}-${index}`}
                        className="w-[320px] md:w-[450px] lg:w-[600px]"
                      >
                        <div className="relative h-[180px] md:h-[270px] lg:h-[350px] rounded-xl overflow-hidden bg-neutral-300 shadow-xl item">
                          <picture className="absolute inset-0 w-full h-full">
                            <img
                              src={src}
                              alt={initialTitle || "Partner update image"}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </picture>
                        </div>
                      </SplideSlide>
                    ))}
                  </Splide>
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-12">
            <div className="gap-x-16 col-span-12 md:col-span-8">
            {middleTitle && <h3 className="mb-4">{middleTitle}</h3>}
            {middleDescription && (
              <div className="mb-10">{parse(middleDescription)}</div>
            )}
            </div>
          </div>
          {/* {weekendData && weekendData.length > 0 && (
            <>
              <h3 className="text-2xl mb-6">
                {language === "AR" ? "جدول نهاية الأسبوع" : "Weekend Schedule"}
              </h3>
              <div className="bg-[#28673C80] p-5 md:p-9 rounded-2xl space-y-4">
                {weekendData.map((item, i) => {
                  const scheduleTitle = getLocalizedFieldValue(item, language, "title") || "";
                  return (
                    <div
                      key={item?._id || i}
                      className="flex flex-col gap-y-5 md:flex-row items-start md:items-center bg-[#28673C] px-4 py-3 rounded-[10px] font-SaudiaLight text-lg"
                    >
                      <p>
                        {language === "AR" ? "التاريخ: " : "Date: "}
                        {formatDate(item?.schedule_date)}
                      </p>
                      <p className="font-SaudiaSemiBold md:mr-auto md:pl-10">{scheduleTitle}</p>
                      <p className="md:pr-10">
                        {language === "AR" ? "الوقت: " : "Time: "}
                        {item?.schedule_time || ""}
                      </p>
                      {item?.result_url ? (
                        <a
                          href={item.result_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-black text-white px-4 py-1 rounded-md min-w-36 h-11 text-center flex items-center justify-center"
                        >
                          {language === "AR" ? "النتائج" : "Results"}
                        </a>
                      ) : (
                        <button className="bg-black text-white px-4 py-1 rounded-md min-w-36 h-11">
                          {language === "AR" ? "النتائج" : "Results"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )} */}
          {bottomDescription && (
            <div className="text-white/90 leading-relaxed mt-14">{parse(bottomDescription)}</div>
          )}
        </div>

        {/* Buy Ticket Button */}
        {/* <div
          className={`col-span-12 inline-flex md:block justify-center md:justify-end z-20 mx-auto md:mr-0 md:col-span-4 transition-all duration-300 ${
            isFixed
              ? "fixed md:relative bottom-4 md:bottom-0 md:mt-28 inset-x-0"
              : "relative"
          }`}
        >
          <div className="flex justify-center md:justify-end md:mb-16">
            {buyTicketUrl ? (
              <a
                href={buyTicketUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-[17px] md:text-2xl text-white bg-black md:bg-transparent border border-black md:border-white px-14 md:px-6 py-3 rounded-md font-SaudiaRegular max-w-72 w-full"
              >
                <img src={BuyTicket} alt={language === "AR" ? "شراء التذاكر" : "Buy Ticket"} className="h-[18px] md:h-auto" /> 
                {language === "AR" ? "شراء التذاكر هنا!" : "Buy tickets here!"}
              </a>
            ) : (
              <button className="flex items-center gap-3 text-[17px] md:text-2xl text-white bg-black md:bg-transparent border border-black md:border-white px-14 md:px-6 py-3 rounded-md font-SaudiaRegular max-w-72 w-full">
                <img src={BuyTicket} alt={language === "AR" ? "شراء التذاكر" : "Buy Ticket"} className="h-[18px] md:h-auto" /> 
                {language === "AR" ? "شراء التذاكر هنا!" : "Buy tickets here!"}
              </button>
            )}
          </div>
        </div> */}
      </div>
    </>
  );
}

export default PartnerUpdateDetail;

